import { Check, Edit, Search, X, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "../../components/misc/Button";
import { Badge } from "../../components/calendar/Badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/misc/Table";
import { Input } from "../../components/misc/Input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../components/misc/Dialog";
import { Limits, Plan, User, UserRole, SubscriptionStatus } from "../types";
import { User as AuthUser } from "../../types/user";
import EditUserOverlay from "./EditUserOverlay";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import { getAuthHeader } from "../../utils/authHeader";
import axios from "axios";
import LoadingScreen from "../../screens/LoadingScreen";
import ReconfirmOverlay from "../../components/misc/ReconfirmOverlay";
import { Tabs, TabsList, TabsTrigger } from "../../components/misc/Tabs";
import LimitsTabOverlay from "./LimitsTabOverlay";
import SimpleTooltip from "../../components/misc/SimpleTooltip";
import { planService, userService } from "../../service";
import useAppDispatch from "../../hooks/useAppDispatch";
import { startImpersonation, setIsAdminView } from "../../store/authSlice";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import EditUserRole from "./EditUserRole";

type ConfirmAction =
    | "pause"
    | "resume"
    | "mls"
    | "removeMLS"
    | "verify-user"
    | null;

const ManageUsers = () => {
    const dispatch = useAppDispatch();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [teamAdmins, setTeamAdmins] = useState<User[]>([]);
    const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isAdd, setIsAdd] = useState<boolean>(false);
    // MB-1374: password-setup link returned by provision-comp-user, shown to
    // the admin to hand to the new user.
    const [provisionResult, setProvisionResult] = useState<{
        email: string;
        passwordSetupLink: string;
    } | null>(null);
    const [actionToConfirm, setActionToConfirm] = useState<ConfirmAction>(null);
    const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(false);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<"userData" | "userLimits" | "userRole">(
        "userData"
    );
    const [allPlans, setAllPlans] = useState<Plan[]>([]);
    // Centralized state for user data and limits
    const [userData, setUserData] = useState<Partial<User> | null>(null);
    const [limits, setLimits] = useState<Limits | null>(null); // Define Limits type accordingly
    const [tableLoading, setTableLoading] = useState<boolean>(true);
    const {
        user: currentUser,
    } = useSelector((state: RootState) => state.auth);

    const handleLoginAsUser = async (user: User) => {
        setImpersonatingUserId(user.id);
        try {
            // Call the backend API to get impersonation token
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const response = await axios.post(
                `${backendUrl}/admin/impersonate`,
                { targetUserId: user.id },
                { headers }
            );

            if (response.status === 200 || response.status === 201) {
                const {
                    impersonationToken,
                    sessionId,
                    impersonatedUser
                } = response.data;

                // Map impersonated user to Auth User type
                const authUser: AuthUser = {
                    _id: impersonatedUser.id,
                    nameFirst: impersonatedUser.nameFirst,
                    nameLast: impersonatedUser.nameLast,
                    authId: impersonatedUser.authId,
                    socialProfileKey: "",
                    createdOn: new Date(),
                    lastLogincreatedOn: new Date(),
                    description: null,
                    password: null,
                    status: user.status,
                    role: user.role,
                    mlsApproved: impersonatedUser.mlsApproved || false,
                    prefSocialNetworks: [],
                    prefFacebookNotify: false,
                    prefAutoApproval: false,
                    prefPostApproval: false,
                    onboarded: user.onboarded,
                    licenseNumber: user.licenseNumber,
                    uid: impersonatedUser.authId,
                    email: impersonatedUser.email,
                    emailVerified: user.verified,
                    displayName: `${impersonatedUser.nameFirst} ${impersonatedUser.nameLast}`,
                    isAnonymous: false,
                    photoURL: null,
                    phoneNumber: user.phoneNumber,
                    providerId: "",
                    timeZone: user.timeZone,
                    socialsConnected: false,
                    listingsCreated: false,
                    mlsWelcome: impersonatedUser.mlsWelcome || false,
                    companyName: user.companyName,
                };

                // Dispatch impersonation with token
                dispatch(startImpersonation({
                    user: authUser,
                    impersonationToken,
                    sessionId
                }));

                // Switch to user mode
                dispatch(setIsAdminView(false));

                toast.success(
                    <CustomToast
                        message={`Now viewing as ${impersonatedUser.nameFirst} ${impersonatedUser.nameLast}`}
                        type="success"
                    />,
                    { autoClose: 3000 }
                );
            }
        } catch (error: unknown) {
            console.error("Failed to start impersonation:", error);
            toast.error(
                <CustomToast
                    message={(error as { response?: { data?: { message?: string } } })?.response?.data?.message || "Failed to login as user"}
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setImpersonatingUserId(null);
        }
    };

    const fetchUsers = async () => {
      setTableLoading(true);
      try {
        const userData = await userService.getAllUsers();
        setUsers(userData);
      } catch (error: unknown) {
        console.error("Failed to fetch users:", error);
        toast.error(
          <CustomToast message="Failed to fetch users." type="error" />,
          {
            autoClose: 3000,
          }
        );
      } finally {
        setTableLoading(false);
      }
    };

    const fetchPlans = async () => {
      try {
        setLoading(true);
        const planData = await planService.getAllPlans();
        setAllPlans(planData);
      } catch (error: unknown) {
        console.error("Failed to fetch plans:", error);
        toast.error(
          <CustomToast message="Failed to fetch plans." type="error" />,
          {
            autoClose: 3000,
          }
        );
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
        fetchUsers();
        fetchPlans();
    }, []);

    useEffect(() => {
        const admins = users.filter((user) => user.role === "TEAM_ADMIN");
        setTeamAdmins(admins || []);
    }, [users]);

    // Filtering Users Based on Search Term
    const filteredUsers = users.filter(
        (user) =>
            `${user.nameFirst} ${user.nameLast}`
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Separate Active and Inactive Users based on Subscription Status
    const activeUsers = filteredUsers.filter(
        (user) =>
            user?.account?.activeSubscription &&
            user.account.activeSubscription.status === SubscriptionStatus.Active
    );

    const inactiveUsers = filteredUsers.filter(
        (user) =>
            user?.account?.activeSubscription &&
            user.account.activeSubscription.status ===
                SubscriptionStatus.Inactive
    );

    // Handle Edit User
    const handleEditUser = (user: User) => {
        setUserData({ ...user });
        setSelectedUser(user);
        // MB-1377: docs created before a limits field existed lack it entirely;
        // seed the default so the Limits tab shows and saves the field instead
        // of silently omitting it.
        setLimits(user.limits ? { autopilotListingLimit: 0, interactiveAiLimitMicros: 0, ...user.limits } : null);
        setActiveTab("userData");
    };

    // Handle Action (Pause/Resume/MLS)
    const handleAction = (action: ConfirmAction, userId: string) => {
        setActionToConfirm(action);
        setIsOverlayOpen(true);
        setSelectedUserId(userId);
    };

    // Close Overlay
    const closeOverlay = () => {
        setIsOverlayOpen(false);
        setActionToConfirm(null);
    };

    // Confirmation Handler based on the Action
    const confirmAction = async () => {
        if (!actionToConfirm || !selectedUserId) return;

        const userId = selectedUserId;
        const userEmail = users.find((user) => user.id === userId)?.email;
        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            setLoading(true);
            closeOverlay();

            let response;
            let updatedUsers: User[] | undefined;

            switch (actionToConfirm) {
                case "resume":
                    response = await axios.patch(
                        `${backendUrl}/admin/resume-user`,
                        { userId },
                        { headers }
                    );
                    if (response.status !== 200)
                        throw new Error("Failed to resume user.");
                    updatedUsers = users.map((user) =>
                        user.id === userId
                            ? { ...user, status: "ACTIVE" }
                            : user
                    );
                    toast.success(
                        <CustomToast
                            message="User resumed successfully."
                            type="success"
                        />,
                        { autoClose: 3000 }
                    );
                    break;

                case "pause":
                    response = await axios.patch(
                        `${backendUrl}/admin/pause-user`,
                        { userId },
                        { headers }
                    );
                    if (response.status !== 200)
                        throw new Error("Failed to pause user.");
                    updatedUsers = users.map((user) =>
                        user.id === userId
                            ? { ...user, status: "PAUSED" }
                            : user
                    );
                    toast.success(
                        <CustomToast
                            message="User paused successfully."
                            type="success"
                        />,
                        { autoClose: 3000 }
                    );
                    break;

                case "mls":
                    response = await axios.patch(
                        `${backendUrl}/admin/update-user`,
                        { userId, mlsApproved: true },
                        { headers }
                    );
                    if (response.status !== 200)
                        throw new Error("Failed to approve MLS.");
                    updatedUsers = users.map((user) =>
                        user.id === userId
                            ? { ...user, mlsApproved: true }
                            : user
                    );
                    toast.success(
                        <CustomToast
                            message="MLS approved successfully."
                            type="success"
                        />,
                        { autoClose: 3000 }
                    );
                    break;

                case "removeMLS":
                    response = await axios.patch(
                        `${backendUrl}/admin/remove-mls`,
                        { userId },
                        { headers }
                    );
                    if (response.status !== 200)
                        throw new Error("Failed to remove MLS.");

                    updatedUsers = users.map((user) =>
                        user.id === userId
                            ? { ...user, mlsApproved: false }
                            : user
                    );
                    toast.success(
                        <CustomToast
                            message="MLS removed successfully."
                            type="success"
                        />,
                        { autoClose: 3000 }
                    );
                    break;

                case "verify-user":
                    response = await axios.post(
                        `${backendUrl}/admin/verify-user`,
                        { email: userEmail },
                        { headers }
                    );
                    if (response.status !== 200)
                        throw new Error("Failed to verify user.");
                    updatedUsers = users;
                    toast.success(
                        <CustomToast
                            message="User Verified successfully."
                            type="success"
                        />,
                        { autoClose: 3000 }
                    );
                    break;

                default:
                    break;
            }

            setUsers(updatedUsers || []);
        } catch (error: unknown) {
            console.error(`Failed to ${actionToConfirm} user:`, error);
            // MB-1532: Remove MLS is refused with 423 while the user's Auto
            // Pilot is still setting up - surface the server's message.
            const refused =
                axios.isAxiosError(error) &&
                error.response?.status === 423 &&
                error.response.data?.message
                    ? String(error.response.data.message)
                    : null;
            toast.error(
                <CustomToast
                    message={refused ?? `Failed to ${actionToConfirm} user.`}
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setLoading(false);
        }
    };

    // Get Overlay Content based on Action
    const getOverlayContent = () => {
        switch (actionToConfirm) {
            case "pause":
                return {
                    title: "Pause User Account",
                    message:
                        "Are you sure you want to pause this user account?",
                    messageTwo:
                        "This will pause the user, and they won't be able to perform any actions on the site. However, their scheduled posts will still be posted.",
                    confirmText: "Pause",
                };
            case "resume":
                return {
                    title: "Resume User Account",
                    message:
                        "Are you sure you want to resume this user account?",
                    messageTwo:
                        "This will resume the user, and they will be able to perform actions on the site.",
                    confirmText: "Resume",
                };
            case "mls":
                return {
                    title: "Approve MLS",
                    message:
                        "Are you sure you want to approve MLS for this user?",
                    messageTwo:
                        "This will approve MLS for the user, and they will be able to access MLS data.",
                    confirmText: "Approve MLS",
                };
            case "removeMLS":
                return {
                    title: "Remove MLS",
                    message:
                        "Are you sure you want to remove MLS for this user?",
                    messageTwo:
                        "This will remove MLS for the user, and they will not be able to access MLS data. If the user has Auto Pilot on, it will be turned off and the listings it manages — including their campaigns and posts — will be deleted by the daily cleanup.",
                    confirmText: "Remove MLS",
                };
            case "verify-user":
                return {
                    title: "Verify User",
                    message: "Are you sure you want to verify this user?",
                    messageTwo:
                        "This will verufy the user on firebase and they will be able to perform actions on the site.",
                    confirmText: "Verify",
                };
            default:
                return {
                    title: "",
                    message: "",
                    messageTwo: "",
                    confirmText: "",
                };
        }
    };

    const { title, message, messageTwo, confirmText } = getOverlayContent();

    // Handle Save Action
    const handleSave = async () => {
        if (!userData) {
            toast.error(
                <CustomToast message="User data is missing." type="error" />,
                { autoClose: 3000 }
            );
            return;
        }

        const headers = await getAuthHeader();
        const backendUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            setIsSaving(true);

            // Update user details
            // MB-1374: adding a user provisions a full comp (non-payment)
            // account — Firebase login, Ayrshare profile, plan + cloned
            // limits, MyBrand/LeadGen — via the documented flow.
            const userResponse = isAdd
                ? await axios.post(
                      `${backendUrl}/admin/provision-comp-user`,
                      {
                          nameFirst: userData.nameFirst,
                          nameLast: userData.nameLast,
                          email: userData.email,
                          planId: userData.plan?.id,
                          mlsApproved: userData.mlsApproved ?? false,
                          timeZone: userData.timeZone,
                          companyName: userData.companyName,
                          mls:
                              typeof userData.mls === "string"
                                  ? userData.mls
                                  : userData?.mls?._id?.toString(),
                      },
                      { headers }
                  )
                : await axios.patch(
                      `${backendUrl}/admin/update-user/`,
                      {
                          userId: userData.id,
                          nameFirst: userData.nameFirst,
                          nameLast: userData.nameLast,
                          email: userData.email,
                          role: userData.role,
                          mlsApproved: userData.mlsApproved,
                          onboarded: userData.onboarded,
                          simulatePosting: userData.simulatePosting,
                          simulateContentGeneration:
                              userData.simulateContentGeneration,
                          licenseNumber: userData.licenseNumber,
                          companyName: userData.companyName,
                          phoneNumber: userData.phoneNumber,
                          broker: userData.broker,
                          brokerLicense: userData.brokerLicense,
                          brokerEmail: userData.brokerEmail,
                          mls:
                              typeof userData.mls === "string"
                                  ? userData.mls
                                  : userData?.mls?._id.toString(),
                          otherMLS: userData.otherMLS,
                          timeZone: userData.timeZone,
                          userPlan: {
                              ...userData.plan,
                              _id: userData.plan?.id,
                          },
                          limits: limits ? { ...limits } : undefined,
                          adminRole: userData.adminRole,
                      },
                      { headers }
                  );

            if (userResponse.status !== 200 && userResponse.status !== 201) {
                throw new Error("Failed to save user details.");
            }

            toast.success(
                <CustomToast
                    message="User details and limits saved successfully."
                    type="success"
                />,
                { autoClose: 3000 }
            );

            // Refresh the user list
            fetchUsers();

            // MB-1374: surface the password-setup link for the new comp user.
            if (isAdd && userResponse.data?.passwordSetupLink) {
                setProvisionResult({
                    email: userResponse.data.email,
                    passwordSetupLink: userResponse.data.passwordSetupLink,
                });
            }

            // Close the dialog
            setSelectedUser(null);
            setIsAdd(false);
            setUserData(null);
            setLimits(null);
        } catch (error: unknown) {
            console.error("Failed to save user:", error);
            const serverMessage =
                axios.isAxiosError(error) &&
                typeof error.response?.data?.message === "string"
                    ? error.response.data.message
                    : null;
            toast.error(
                <CustomToast
                    message={
                        serverMessage ??
                        "Failed to save user details and limits."
                    }
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setIsSaving(false);
        }
    };

    const AdminRoleBadge = ({ user }: { user: User }) => {
        if (user.adminRole === "ADMIN" || user.isAdmin === true || user.isAdmin === "true") {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                    Admin
                </span>
            );
        }
        if (user.adminRole === "CREATOR") {
            return (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Creator
                </span>
            );
        }
        return null;
    };

    return (
        <div className="flex flex-col h-full p-4 overflow-x-hidden">
            {loading && <LoadingScreen />}
            {provisionResult && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl space-y-4">
                        <h2 className="text-xl font-bold text-primary">
                            Comp user created
                        </h2>
                        <p className="text-sm text-neutral-600">
                            Account for{" "}
                            <strong>{provisionResult.email}</strong> is fully
                            provisioned (login, plan, Ayrshare profile). Send
                            them this one-time link to set their password:
                        </p>
                        <input
                            readOnly
                            value={provisionResult.passwordSetupLink}
                            className="w-full border rounded-md px-2 py-1 text-xs"
                            onFocus={(e) => e.target.select()}
                        />
                        <div className="flex justify-end space-x-2">
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    navigator.clipboard.writeText(
                                        provisionResult.passwordSetupLink
                                    )
                                }
                            >
                                Copy Link
                            </Button>
                            <Button onClick={() => setProvisionResult(null)}>
                                Done
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <ReconfirmOverlay
                isOpen={isOverlayOpen}
                onClose={closeOverlay}
                onConfirm={confirmAction}
                title={title}
                message={message}
                messageTwo={messageTwo}
                confirmText={confirmText}
            />
            <div className="flex flex-col flex-1 min-w-0 bg-white py-6 px-4 sm:px-10 rounded shadow overflow-y-auto overflow-x-hidden">
                {/* Header */}
                <h2 className="text-2xl font-bold text-primary mb-4">
                    Manage Users
                </h2>

                {/* Search and Add Button */}
                <div className="flex flex-wrap items-center gap-3 mb-6">
                    <div className="relative w-64 shrink-0">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => setSearchTerm(e.target.value)}
                            className="pl-8 w-full"
                        />
                    </div>
                    <Button
                        className="shrink-0"
                        onClick={() => {
                            setUserData({
                                nameFirst: "",
                                nameLast: "",
                                email: "",
                                role: UserRole.USER,
                                mlsApproved: false,
                                onboarded: false,
                                licenseNumber: "",
                                companyName: "",
                                phoneNumber: "",
                                broker: "",
                                brokerLicense: "",
                                brokerEmail: "",
                                mls: "",
                                otherMLS: "",
                                timeZone: "",
                                plan: allPlans[0],
                            });
                            setLimits(null);
                            setIsAdd(true);
                            setSelectedUser(null);
                        }}
                        disabled={currentUser?.adminRole === "CREATOR"}
                    >
                        Add New User
                    </Button>
                </div>

                {/* Active Users Table */}
                <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-700 mb-4">
                        Active Users
                    </h3>
                    {!loading && tableLoading ? (
                        <div className="flex items-center justify-center py-8 h-[50vh]">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : activeUsers.length === 0 ? (
                        <p>No active users found.</p>
                    ) : (
                        <div className="overflow-x-auto rounded border border-gray-100">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="whitespace-nowrap">Name</TableHead>
                                        <TableHead className="whitespace-nowrap">Email</TableHead>
                                        <TableHead className="whitespace-nowrap">Role</TableHead>
                                        <TableHead className="text-center whitespace-nowrap">Administrator Privileges</TableHead>
                                        <TableHead className="whitespace-nowrap">MLS</TableHead>
                                        <TableHead className="whitespace-nowrap">MLS Approved</TableHead>
                                        <TableHead className="whitespace-nowrap">Status</TableHead>
                                        <TableHead className="whitespace-nowrap min-w-[480px]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {activeUsers.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell className="whitespace-nowrap">
                                                {`${user.nameFirst} ${user.nameLast}`}
                                                {user.simulatePosting && (
                                                    <Badge className="ml-2">
                                                        TESTING
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">{user.email}</TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {user.role
                                                    ?.split("_")
                                                    .map(
                                                        (word) =>
                                                            word.charAt(0) +
                                                            word
                                                                .slice(1)
                                                                .toLowerCase()
                                                    )
                                                    .join(" ")}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <AdminRoleBadge user={user} />
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {typeof user.mls === "string"
                                                    ? user.mls
                                                    : user?.mls?.name ||
                                                      user.otherMLS}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {user.mlsApproved ? (
                                                    <Check className="text-green-500 mx-auto" />
                                                ) : (
                                                    <X className="text-red-500 mx-auto" />
                                                )}
                                            </TableCell>
                                            <TableCell className="whitespace-nowrap">
                                                {user.status ? (
                                                    <Badge
                                                        variant={
                                                            user.status === "ACTIVE"
                                                                ? "primary"
                                                                : user.status === "PAUSED"
                                                                ? "secondary"
                                                                : "default"
                                                        }
                                                    >
                                                        {user.status}
                                                    </Badge>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-row items-center flex-nowrap gap-2">
                                                    <SimpleTooltip
                                                        className="w-auto"
                                                        message="You can't login as another admin"
                                                        visible={user.isAdmin === true}
                                                    >
                                                        <Button
                                                            size="sm"
                                                            variant="default"
                                                            className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                                                            onClick={() => handleLoginAsUser(user)}
                                                            disabled={user.isAdmin === true || user.isAdmin === "true" || impersonatingUserId === user.id || currentUser?._id === user.id}
                                                        >
                                                            {impersonatingUserId === user.id ? (
                                                                <>
                                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                                    Logging in...
                                                                </>
                                                            ) : (
                                                                "Login as"
                                                            )}
                                                        </Button>
                                                    </SimpleTooltip>
                                                    <Button
                                                        size="sm"
                                                        className="whitespace-nowrap"
                                                        onClick={() => handleEditUser(user)}
                                                        disabled={currentUser?.adminRole === "CREATOR"}
                                                    >
                                                        <Edit className="w-4 h-4 mr-2" />
                                                        Edit
                                                    </Button>
                                                    {user.status === "ACTIVE" ? (
                                                        <Button
                                                            size="sm"
                                                            className="whitespace-nowrap"
                                                            onClick={() => handleAction("pause", user.id)}
                                                            disabled={currentUser?.adminRole === "CREATOR"}
                                                        >
                                                            Pause
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="sm"
                                                            className="whitespace-nowrap"
                                                            onClick={() => handleAction("resume", user.id)}
                                                            disabled={currentUser?.adminRole === "CREATOR"}
                                                        >
                                                            Resume
                                                        </Button>
                                                    )}
                                                    {!user.mlsApproved ? (
                                                        <SimpleTooltip
                                                            className="w-auto"
                                                            message="This MLS is not available."
                                                            visible={!user.mls}
                                                        >
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                className="whitespace-nowrap"
                                                                disabled={!user.mls || currentUser?.adminRole === "CREATOR"}
                                                                onClick={() => handleAction("mls", user.id)}
                                                            >
                                                                MLS
                                                            </Button>
                                                        </SimpleTooltip>
                                                    ) : (
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            className="whitespace-nowrap"
                                                            onClick={() => handleAction("removeMLS", user.id)}
                                                            disabled={currentUser?.adminRole === "CREATOR"}
                                                        >
                                                            Remove MLS
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="destructive"
                                                        size="sm"
                                                        className="whitespace-nowrap"
                                                        onClick={() => handleAction("verify-user", user.id)}
                                                        disabled={currentUser?.adminRole === "CREATOR"}
                                                    >
                                                        Verify User
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                {/* Inactive Users Table */}
                {!tableLoading && (
                    <div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-4">
                            Inactive Users
                        </h3>
                        {inactiveUsers.length === 0 ? (
                            <p>No inactive users found.</p>
                        ) : (
                            <div className="overflow-x-auto rounded border border-gray-100">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="whitespace-nowrap">Name</TableHead>
                                            <TableHead className="whitespace-nowrap">Email</TableHead>
                                            <TableHead className="whitespace-nowrap">Role</TableHead>
                                            <TableHead className="text-center whitespace-nowrap">Administrator Privileges</TableHead>
                                            <TableHead className="whitespace-nowrap">MLS</TableHead>
                                            <TableHead className="whitespace-nowrap">MLS Approved</TableHead>
                                            <TableHead className="whitespace-nowrap">Status</TableHead>
                                            <TableHead className="whitespace-nowrap min-w-[320px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {inactiveUsers.map((user) => (
                                            <TableRow key={user.id}>
                                                <TableCell className="whitespace-nowrap">{`${user.nameFirst} ${user.nameLast}`}</TableCell>
                                                <TableCell className="whitespace-nowrap">{user.email}</TableCell>
                                                <TableCell className="whitespace-nowrap">
                                                    {user.role
                                                        ?.split("_")
                                                        .map(
                                                            (word) =>
                                                                word.charAt(0) +
                                                                word
                                                                    .slice(1)
                                                                    .toLowerCase()
                                                        )
                                                        .join(" ")}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    <AdminRoleBadge user={user} />
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap">
                                                    {typeof user.mls === "string"
                                                        ? user.mls
                                                        : user?.mls?.name ||
                                                          user.otherMLS}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {user.mlsApproved ? (
                                                        <Check className="text-green-500 mx-auto" />
                                                    ) : (
                                                        <X className="text-red-500 mx-auto" />
                                                    )}
                                                </TableCell>
                                                <TableCell className="whitespace-nowrap">
                                                    {user.status ? (
                                                        <Badge
                                                            variant={
                                                                user.status === "ACTIVE"
                                                                    ? "primary"
                                                                    : user.status === "PAUSED"
                                                                    ? "secondary"
                                                                    : "default"
                                                            }
                                                        >
                                                            {user.status}
                                                        </Badge>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-row items-center flex-nowrap gap-2">
                                                        <Button
                                                            size="sm"
                                                            className="whitespace-nowrap"
                                                            onClick={() => handleEditUser(user)}
                                                            disabled={currentUser?.adminRole === "CREATOR"}
                                                        >
                                                            <Edit className="w-4 h-4 mr-2" />
                                                            Edit
                                                        </Button>
                                                        {user.status === "ACTIVE" ? (
                                                            <Button
                                                                size="sm"
                                                                className="whitespace-nowrap"
                                                                onClick={() => handleAction("pause", user.id)}
                                                                disabled={currentUser?.adminRole === "CREATOR"}
                                                            >
                                                                Pause
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                className="whitespace-nowrap"
                                                                onClick={() => handleAction("resume", user.id)}
                                                                disabled={currentUser?.adminRole === "CREATOR"}
                                                            >
                                                                Resume
                                                            </Button>
                                                        )}
                                                        {!user.mlsApproved ? (
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                className="whitespace-nowrap"
                                                                onClick={() => handleAction("mls", user.id)}
                                                                disabled={currentUser?.adminRole === "CREATOR"}
                                                            >
                                                                MLS
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                className="whitespace-nowrap"
                                                                onClick={() => handleAction("removeMLS", user.id)}
                                                                disabled={currentUser?.adminRole === "CREATOR"}
                                                            >
                                                                Remove MLS
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Single Dialog Instance */}
            {(selectedUser || isAdd) && (
                <Dialog
                    open={Boolean(selectedUser) || isAdd}
                    onOpenChange={(isOpen) => {
                        if (!isOpen) {
                            setSelectedUser(null);
                            setIsAdd(false);
                            setUserData(null);
                            setLimits(null);
                            setActiveTab("userData");
                        }
                    }}
                >
                    <DialogContent className="sm:max-w-[800px] bg-white relative overflow-hidden flex flex-col max-h-[90vh]">
                        {isSaving && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm z-50 rounded-lg">
                                <div className="loader" />
                            </div>
                        )}
                        <DialogHeader>
                            <DialogTitle>
                                {isAdd ? "Add New User" : "Edit User"}
                            </DialogTitle>
                        </DialogHeader>
                        <Tabs
                            value={activeTab}
                            onValueChange={(value: string) =>
                                setActiveTab(value as "userData" | "userLimits" | "userRole")
                            }
                            className="mt-4 flex flex-col min-h-0 flex-1"
                        >
                            {/* Limits come from the plan and admin privileges
                                are not part of provisioning, so both tabs
                                would be discarded on add. */}
                            <TabsList>
                                <TabsTrigger value="userData">
                                    User Data
                                </TabsTrigger>
                                {!isAdd && (
                                    <>
                                        <TabsTrigger value="userLimits">
                                            User Limits
                                        </TabsTrigger>
                                        <TabsTrigger value="userRole">
                                            Administrator Privileges
                                        </TabsTrigger>
                                    </>
                                )}
                            </TabsList>

                            <div className="overflow-y-auto flex-1 pr-1">
                            {/* Pass userData and setUserData to EditUserOverlay */}
                            <EditUserOverlay
                                userData={userData}
                                setUserData={setUserData}
                                teamAdmins={teamAdmins}
                                isAdd={isAdd}
                                allPlans={allPlans}
                            />

                            {!isAdd && (
                                <>
                                    {/* Pass limits and setLimits to LimitsTabOverlay */}
                                    <LimitsTabOverlay
                                        limits={limits}
                                        setLimits={setLimits}
                                    />

                                    <EditUserRole
                                        userData={userData}
                                        setUserData={setUserData}
                                    />
                                </>
                            )}
                            </div>
                        </Tabs>

                        {/* Dialog Footer with Save and Cancel Buttons */}
                        <div className="flex justify-end mt-4 pt-2 border-t">
                            <Button
                                variant="secondary"
                                className="mr-2"
                                onClick={() => {
                                    setSelectedUser(null);
                                    setIsAdd(false);
                                    setUserData(null);
                                    setLimits(null);
                                    setActiveTab("userData");
                                }}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={isSaving}>
                                Save
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};

export default ManageUsers;
